import React, { useEffect, useState } from 'react';
import {
  FiChevronDown,
  FiDownload,
  FiEye,
  FiFile,
  FiMail,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiStar,
  FiTrash2,
  FiUser,
  FiUsers,
  FiX
} from 'react-icons/fi';
import apiService from '../services/api';

const Resumes = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    service: 'all',
    priority: 'all'
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 0,
    total: 0,
    limit: 10
  });
  
  // Modals
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingContact, setViewingContact] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);

  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [filters, pagination.current, pagination.limit]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.limit,
        ...filters
      };
      
      // Remove 'all' values
      Object.keys(params).forEach(key => {
        if (params[key] === 'all') delete params[key];
      });

      const response = await apiService.getContacts(params);
      setContacts(response.data.contacts);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.getContactStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusUpdate = async (contactId, newStatus) => {
    try {
      await apiService.updateContactStatus(contactId, newStatus);
      fetchContacts();
      fetchStats();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteContact = async () => {
    try {
      await apiService.deleteContact(deletingContact._id);
      setShowDeleteModal(false);
      setDeletingContact(null);
      fetchContacts();
      fetchStats();
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500/20 text-blue-400 border-blue-400/30',
      'in-progress': 'bg-yellow-500/20 text-yellow-400 border-yellow-400/30',
      responded: 'bg-green-500/20 text-green-400 border-green-400/30',
      closed: 'bg-gray-500/20 text-gray-400 border-gray-400/30'
    };
    return colors[status] || colors.new;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'new': 'New',
      'in-progress': 'In Progress',
      'responded': 'Responded',
      'closed': 'Closed'
    };
    return labels[status] || status;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-500/20 text-red-400',
      high: 'bg-orange-500/20 text-orange-400',
      medium: 'bg-yellow-500/20 text-yellow-400',
      low: 'bg-green-500/20 text-green-400'
    };
    return colors[priority] || colors.medium;
  };

  const renderStars = (rating = 0) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FiStar
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-400'}`}
      />
    ));
  };

  if (loading && contacts.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <span className="text-white text-lg">Loading contacts...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text text-transparent font-display mb-2">
                Contact Management
              </h1>
              <p className="text-slate-300 text-lg">
                Manage contact inquiries and resume submissions
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={fetchContacts}
                className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 rounded-lg text-purple-400 text-sm transition-all duration-300 flex items-center gap-2 hover:scale-105"
              >
                <FiRefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-500/20 border border-red-400/30 rounded-lg flex items-center justify-between text-red-400">
              <span>{error}</span>
              <button onClick={() => setError(null)}>
                <FiX className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <FiUsers className="w-8 h-8 text-cyan-400" />
                <span className="text-3xl font-bold text-white">{stats.totals?.total || 0}</span>
              </div>
              <h3 className="text-slate-300 text-sm font-medium">Total Contacts</h3>
            </div>
            
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <FiMail className="w-8 h-8 text-blue-400" />
                <span className="text-3xl font-bold text-white">{stats.totals?.new || 0}</span>
              </div>
              <h3 className="text-slate-300 text-sm font-medium">New Inquiries</h3>
            </div>
            
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <FiFile className="w-8 h-8 text-green-400" />
                <span className="text-3xl font-bold text-white">{stats.totals?.responded || 0}</span>
              </div>
              <h3 className="text-slate-300 text-sm font-medium">Responded</h3>
            </div>
            
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <FiUser className="w-8 h-8 text-purple-400" />
                <span className="text-3xl font-bold text-white">{stats.totals?.inProgress || 0}</span>
              </div>
              <h3 className="text-slate-300 text-sm font-medium">In Progress</h3>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search contacts by name, email, or company..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-400/30"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="in-progress">In Progress</option>
              <option value="responded">Responded</option>
              <option value="closed">Closed</option>
            </select>

            {/* Service Filter */}
            <select
              value={filters.service}
              onChange={(e) => handleFilterChange('service', e.target.value)}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
            >
              <option value="all">All Services</option>
              <option value="IT Staffing">IT Staffing</option>
              <option value="Project Services">Project Services</option>
              <option value="Cloud & Infrastructure">Cloud & Infrastructure</option>
              <option value="Cyber Security">Cyber Security</option>
              <option value="AI & Intelligent Agent Development">AI Development</option>
              <option value="Enterprise Applications">Enterprise Applications</option>
            </select>

            {/* Priority Filter */}
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
            >
              <option value="all">All Priority</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>

        {/* Contacts Table */}
        <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50 border-b border-slate-600/50">
                <tr>
                  <th className="p-4 text-left text-slate-300 font-medium">Contact</th>
                  <th className="p-4 text-left text-slate-300 font-medium">Service</th>
                  <th className="p-4 text-left text-slate-300 font-medium">Status</th>
                  <th className="p-4 text-left text-slate-300 font-medium">Priority</th>
                  <th className="p-4 text-left text-slate-300 font-medium">Date</th>
                  <th className="p-4 text-left text-slate-300 font-medium">Resume</th>
                  <th className="p-4 text-left text-slate-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-600/30">
                {contacts.map((contact) => (
                  <tr key={contact._id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="p-4">
                      <div>
                        <div className="text-white font-medium">{contact.name}</div>
                        <div className="text-slate-400 text-sm flex items-center gap-2">
                          <FiMail className="w-3 h-3" />
                          {contact.email}
                        </div>
                        {contact.phone && (
                          <div className="text-slate-400 text-sm flex items-center gap-2">
                            <FiPhone className="w-3 h-3" />
                            {contact.phone}
                          </div>
                        )}
                        {contact.company && (
                          <div className="text-slate-500 text-xs">{contact.company}</div>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                        {contact.service || 'General Inquiry'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="relative">
                        <select
                          value={contact.status}
                          onChange={(e) => handleStatusUpdate(contact._id, e.target.value)}
                          className={`px-2 py-1 rounded text-xs border appearance-none bg-transparent ${getStatusColor(contact.status)}`}
                        >
                          <option value="new">New</option>
                          <option value="in-progress">In Progress</option>
                          <option value="responded">Responded</option>
                          <option value="closed">Closed</option>
                        </select>
                        <FiChevronDown className="absolute right-1 top-1/2 transform -translate-y-1/2 w-3 h-3 pointer-events-none" />
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs ${getPriorityColor(contact.priority)}`}>
                        {contact.priority}
                      </span>
                    </td>
                    <td className="p-4 text-slate-400 text-sm">
                      {new Date(contact.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      {contact.cvFile?.filename ? (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/uploads/cvs/${contact.cvFile.filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
                        >
                          <FiFile className="w-4 h-4" />
                          <span className="text-xs">View CV</span>
                        </a>
                      ) : (
                        <span className="text-slate-500 text-xs">No CV</span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setViewingContact(contact);
                            setShowViewModal(true);
                          }}
                          className="p-1 text-slate-400 hover:text-cyan-400 transition-colors"
                          title="View Details"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setDeletingContact(contact);
                            setShowDeleteModal(true);
                          }}
                          className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                          title="Delete Contact"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {contacts.length === 0 && !loading && (
              <div className="text-center py-12 text-slate-400">
                <FiUsers className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No contacts found</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="p-4 border-t border-slate-600/30 flex items-center justify-between">
              <div className="text-slate-400 text-sm">
                Showing {((pagination.current - 1) * pagination.limit) + 1} to {Math.min(pagination.current * pagination.limit, pagination.total)} of {pagination.total} contacts
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                  disabled={!pagination.hasPrev}
                  className="px-3 py-1 bg-slate-700 text-slate-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
                >
                  Previous
                </button>
                <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded">
                  {pagination.current} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                  disabled={!pagination.hasNext}
                  className="px-3 py-1 bg-slate-700 text-slate-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* View Contact Modal */}
      {showViewModal && viewingContact && (
        <ViewContactModal
          contact={viewingContact}
          onClose={() => {
            setShowViewModal(false);
            setViewingContact(null);
          }}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && deletingContact && (
        <DeleteModal
          title="Delete Contact"
          message={`Are you sure you want to delete ${deletingContact.name}? This action cannot be undone.`}
          onClose={() => {
            setShowDeleteModal(false);
            setDeletingContact(null);
          }}
          onConfirm={handleDeleteContact}
        />
      )}
    </div>
  );
};

// View Contact Modal Component
const ViewContactModal = ({ contact, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-2xl border border-slate-600 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">Contact Details</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Contact Info */}
          <div className="bg-slate-700/30 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-white mb-3">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-1">Name</label>
                <p className="text-white">{contact.name}</p>
              </div>
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-1">Email</label>
                <p className="text-white">{contact.email}</p>
              </div>
              {contact.phone && (
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-1">Phone</label>
                  <p className="text-white">{contact.phone}</p>
                </div>
              )}
              {contact.company && (
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-1">Company</label>
                  <p className="text-white">{contact.company}</p>
                </div>
              )}
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-1">Service</label>
                <p className="text-white">{contact.service || 'General Inquiry'}</p>
              </div>
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-1">Status</label>
                <p className="text-white capitalize">{contact.status}</p>
              </div>
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-1">Priority</label>
                <p className="text-white capitalize">{contact.priority}</p>
              </div>
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-1">Date</label>
                <p className="text-white">{new Date(contact.createdAt).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="bg-slate-700/30 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-white mb-3">Message</h4>
            <p className="text-slate-300 whitespace-pre-wrap">{contact.message}</p>
          </div>

          {/* CV File */}
          {contact.cvFile?.filename && (
            <div className="bg-slate-700/30 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-white mb-3">Resume/CV</h4>
              <div className="flex items-center gap-4">
                <FiFile className="w-8 h-8 text-cyan-400" />
                <div>
                  <p className="text-white font-medium">{contact.cvFile.originalName}</p>
                  <p className="text-slate-400 text-sm">
                    {contact.cvFile.size ? Math.round(contact.cvFile.size / 1024) + ' KB' : 'Size unknown'}
                  </p>
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/uploads/cvs/${contact.cvFile.filename}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/30 rounded-lg text-cyan-400 text-sm transition-all duration-300 flex items-center gap-2"
                >
                  <FiDownload className="w-4 h-4" />
                  Download
                </a>
              </div>
            </div>
          )}

          {/* Technical Details */}
          {(contact.ipAddress || contact.userAgent) && (
            <div className="bg-slate-700/30 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-white mb-3">Technical Details</h4>
              <div className="space-y-2">
                {contact.ipAddress && (
                  <div>
                    <label className="block text-slate-300 text-sm font-medium">IP Address</label>
                    <p className="text-slate-400 text-sm font-mono">{contact.ipAddress}</p>
                  </div>
                )}
                {contact.userAgent && (
                  <div>
                    <label className="block text-slate-300 text-sm font-medium">User Agent</label>
                    <p className="text-slate-400 text-sm font-mono break-all">{contact.userAgent}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-500 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Delete Modal Component
const DeleteModal = ({ title, message, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-slate-600">
        <h3 className="text-xl font-bold text-white mb-4">{title}</h3>
        <p className="text-slate-300 mb-6">{message}</p>
        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Resumes; 